// Angular
import {
    Component,
    ElementRef,
    inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

// Material
import { MatIconModule } from '@angular/material/icon';

// AG-Grid
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
    IServerSideDatasource,
    IServerSideGetRowsRequest,
} from 'ag-grid-community';

// Services
import { DiscrepancyService } from '@services/api/discrepancy/discrepancy.service';
import { TenantDataService } from '@services/data/tenant-data.service';
import { WebsocketService } from '@services/websocket/websocket.service';
import { UserDataService } from '@services/data/user-data.service';

// Components
import { TextButtonComponent } from '@shared/button/text-button/text-button.component';
import { FlatButtonComponent } from '@shared/button/flat-button/flat-button.component';
import { TooltipComponent } from '@shared/tooltip/tooltip.component';
import { TableComponent } from '@grid-table/table.component';
import { LoaderComponent } from '@shared/loader/loader.component';

// Directives
import { FeatureFlagDirective } from '@directives/feature-flag.directive';

// Models
import { AddCommentRequest, Comment } from '@models/discrepancy';
import { NewCommentEvent } from '@models/discrepancy/comment';
import { EscalationLevel } from '@models/discrepancy/escalation-status';
import { RoleLabel } from '@models/user';

// Helpers
import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';
import { convertToLocalTimeAndFormat } from '@helpers/time';
import { getSortQuery } from '@helpers/table';

// Pipes
import { UtcToLocalDatePipe } from '@pipes/utc-to-local-date.pipe';

// Constants
import { ExportFileNames } from '@constants/table';

// RxJS
import { filter } from 'rxjs';

type ActiveTab = 'comments' | 'activityLog' | 'escalation' | null;
type DiscrepancyCollapsiblesVariant = 'main' | 'station';

// Constants
const COMMENTS_SCROLL_ENABLE_DELAY_MS = 500;
const COMMENTS_PAGE_SIZE = 10;
const COMMENTS_BOTTOM_THRESHOLD_PX = 2;
const COMMENT_MAX_LENGTH = 100;

// Subscription Keys
const SUBSCRIPTION_KEY_LOAD_COMMENTS = 'load-comments';
const SUBSCRIPTION_KEY_PREFETCH_COMMENTS_COUNT = 'prefetch-comments-count';
const SUBSCRIPTION_KEY_ADD_COMMENT = 'add-comment';
const SUBSCRIPTION_KEY_ESCALATION_LEVELS = 'escalation-levels';
const SUBSCRIPTION_KEY_ESCALATION_START = 'escalation-start';
const SUBSCRIPTION_KEY_TENANT_SETTINGS = 'tenant-settings';
const SUBSCRIPTION_KEY_WEBSOCKET_COMMENTS = 'websocket-comments';

@Component({
    selector: 'app-discrepancy-collapsibles',
    templateUrl: 'discrepancy-collapsibles.component.html',
    styleUrl: 'discrepancy-collapsibles.component.scss',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatIconModule,
        TextButtonComponent,
        FlatButtonComponent,
        TableComponent,
        TooltipComponent,
        LoaderComponent,
        UtcToLocalDatePipe,
        FeatureFlagDirective,
    ],
})
export class DiscrepancyCollapsiblesComponent implements OnInit, OnDestroy {
    // ============================================================================
    // Protected Properties
    // ============================================================================

    protected RoleLabel = RoleLabel;
    protected isCommentsScrollEnabled = false;
    protected isCommentsScrolledPast50px = false;

    // ============================================================================
    // Input/View Child Properties
    // ============================================================================

    @Input({ required: true })
    discrepancyId!: number;

    @Input()
    variant: DiscrepancyCollapsiblesVariant = 'main';

    @Input()
    showComments = true;

    @Input()
    showEscalation = true;

    @ViewChild('contentEnd')
    contentEnd?: ElementRef<HTMLElement>;

    @ViewChild('activityLogEnd')
    activityLogEnd?: ElementRef<HTMLElement>;

    @ViewChild('commentsList')
    commentsList?: ElementRef<HTMLElement>;

    @ViewChild('commentTextarea')
    commentTextarea?: ElementRef<HTMLTextAreaElement>;

    // ============================================================================
    // Public Properties
    // ============================================================================

    activeTab: ActiveTab = null;

    // ============================================================================
    // Comments Properties
    // ============================================================================

    comments: Comment[] = [];
    totalCommentsCount = 0;
    commentsPageSize = COMMENTS_PAGE_SIZE;
    commentsPageIndex = 1;
    commentsOrder = 'desc';
    isLoadingComments = false;

    newCommentControl = new FormControl<string>('', { nonNullable: true });
    commentMaxLength = COMMENT_MAX_LENGTH;
    isAddingComment = false;

    // ============================================================================
    // Activity Log Properties
    // ============================================================================

    activityLogColumnDefs: ColDef[] = this.getActivityLogColumnDefs();
    activityLogGridOptions!: GridOptions;

    // ============================================================================
    // Escalation Properties
    // ============================================================================

    escalationError = false;
    escalationLevels: EscalationLevel[] = [];
    escalationStarted = false;
    isEscalationToggleVisible = false;

    // ============================================================================
    // Private Properties
    // ============================================================================

    private readonly discrepancyService = inject(DiscrepancyService);
    private readonly tenantDataService = inject(TenantDataService);
    private readonly elementRef = inject(ElementRef);
    private readonly websocketService = inject(WebsocketService);
    private readonly userDataService = inject(UserDataService);
    private userId: number | undefined;

    private _subscriptions = new SubscriptionList() as SubscriptionListType;
    private activityLogGridApi!: GridApi;
    private commentsHoverTimer: ReturnType<typeof setTimeout> | null = null;
    private scrollContainer: HTMLElement | null = null;
    private hasMoreComments = true;

    // ============================================================================
    // Getters
    // ============================================================================

    get isStationVariant(): boolean {
        return this.variant === 'station';
    }

    // ============================================================================
    // Public Methods
    // ============================================================================

    protected shouldShowAuthorTooltip(fullName: string): boolean {
        // Approximately 270px with font-weight 600, text-xs, uppercase
        // Each character is roughly 7px, so 270px / 7 ≈ 38 characters
        return fullName.length > 38;
    }

    ngOnInit(): void {
        this.userId = this.userDataService.currentUser?.id;
        this.listenForNewComments();

        const fileNameToExport = `${ExportFileNames.ACTIVITY_LOG} - Discrepancy ${this.discrepancyId} `;

        this.activityLogGridOptions = {
            defaultExcelExportParams: { fileName: fileNameToExport },
            defaultCsvExportParams: { fileName: fileNameToExport },
        };

        if (this.showComments) {
            this.watchTenantSettings();
            this.prefetchCommentsCount();
        }

        if (this.showEscalation && !this.isStationVariant) {
            this.checkEscalationAvailability();
        }

        this.trackParentScroll();
    }

    ngOnDestroy(): void {
        this._subscriptions.unsubscribeAllSafe();

        if (this.commentsHoverTimer) {
            clearTimeout(this.commentsHoverTimer);
            this.commentsHoverTimer = null;
        }
    }

    // ============================================================================
    // Comments
    // ============================================================================

    get commentsButtonText() {
        const count = ` (${this.totalCommentsCount})`;
        return `${this.activeTab === 'comments' ? 'Hide' : 'Show'} Comments${count}`;
    }

    private prefetchCommentsCount() {
        const params = {
            pageSize: COMMENTS_PAGE_SIZE,
            pageIndex: 1,
            order: this.commentsOrder,
        };

        this._subscriptions[SUBSCRIPTION_KEY_PREFETCH_COMMENTS_COUNT] =
            this.discrepancyService
                .getDiscrepancyComments(this.discrepancyId, params)
                .subscribe({
                    next: (data) => {
                        this.totalCommentsCount = data.totalCount;
                    },
                });
    }

    protected toggleComments() {
        if (this.activeTab === 'comments') {
            this.activeTab = null;
            this.disableCommentsScroll();
        } else {
            this.activeTab = 'comments';
            this.disableCommentsScroll();
            if (this.comments.length === 0) {
                this.loadComments(true);
            }
        }
    }

    protected onCommentsMouseEnter() {
        if (this.commentsHoverTimer) {
            clearTimeout(this.commentsHoverTimer);
        }

        this.commentsHoverTimer = setTimeout(() => {
            if (this.activeTab === 'comments') {
                this.isCommentsScrollEnabled = true;
                this.disableBodyScroll();
                // If scroll was not actually disabled (content doesn't need it), disable comments scroll
                if (!this.commentsListNeedsScroll()) {
                    this.isCommentsScrollEnabled = false;
                }
            }
        }, COMMENTS_SCROLL_ENABLE_DELAY_MS);
    }

    protected onCommentsMouseLeave() {
        this.disableCommentsScroll();
        this.enableBodyScroll();
    }

    protected submitComment() {
        if (this.isCommentSaveDisabled) {
            return;
        }

        this.isAddingComment = true;
        const payload = new AddCommentRequest(this.newCommentControl.value);

        this._subscriptions[SUBSCRIPTION_KEY_ADD_COMMENT] =
            this.discrepancyService
                .addDiscrepancyComment(this.discrepancyId, payload)
                .subscribe({
                    next: () => {
                        this.clearComment();
                        this.loadComments(true);
                    },
                    complete: () => {
                        this.isAddingComment = false;
                    },
                });
    }

    /**
     * Subscribes to real-time comment events from WebSocket.
     * Filters to only this discrepancy and excludes own comments / duplicates.
     */
    private listenForNewComments() {
        this._subscriptions[SUBSCRIPTION_KEY_WEBSOCKET_COMMENTS] =
            this.websocketService.commentReceived$
                .pipe(
                    filter(
                        (event: NewCommentEvent) =>
                            event.discrepancyId === this.discrepancyId &&
                            event.comment.commentator.id !== this.userId &&
                            !this.comments.some(
                                (c) => c.id === event.comment.id,
                            ),
                    ),
                )
                .subscribe((event: NewCommentEvent) => {
                    if (this.activeTab === 'comments') {
                        this.comments.unshift(event.comment);
                    }
                    this.totalCommentsCount++;
                });
    }

    private disableCommentsScroll() {
        if (this.commentsHoverTimer) {
            clearTimeout(this.commentsHoverTimer);
            this.commentsHoverTimer = null;
        }

        this.isCommentsScrollEnabled = false;
    }

    private disableBodyScroll() {
        if (!this.commentsListNeedsScroll()) {
            return;
        }

        const scrollbarWidth =
            window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = `${scrollbarWidth}px`;

        const scrollContainer = this.findScrollableParent(
            this.elementRef.nativeElement,
        );

        if (scrollContainer) {
            scrollContainer.style.overflow = 'hidden';
            this.scrollContainer = scrollContainer;
        }
    }

    private commentsListNeedsScroll(): boolean {
        const listElement = this.commentsList?.nativeElement;
        if (!listElement) {
            return false;
        }
        return listElement.scrollHeight > listElement.clientHeight;
    }

    private enableBodyScroll() {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';

        if (this.scrollContainer) {
            this.scrollContainer.style.overflow = '';
            this.scrollContainer = null;
        }
    }

    private findScrollableParent(element: HTMLElement): HTMLElement | null {
        let parent = element.parentElement;

        while (parent && parent !== document.body) {
            const overflowY = window.getComputedStyle(parent).overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll') {
                return parent;
            }
            parent = parent.parentElement;
        }

        return null;
    }

    private trackParentScroll(): void {
        const parentScroll = this.findScrollableParent(
            this.elementRef.nativeElement,
        );
        if (!parentScroll) {
            return;
        }

        const onParentScroll = () => {
            this.isCommentsScrolledPast50px = parentScroll.scrollTop >= 50;
        };

        parentScroll.addEventListener('scroll', onParentScroll);

        this.ngOnDestroy = (() => {
            const original = this.ngOnDestroy.bind(this);
            return () => {
                parentScroll.removeEventListener('scroll', onParentScroll);
                original();
            };
        })();
    }

    protected formatDate(date: string): string {
        return convertToLocalTimeAndFormat(date);
    }

    protected get isCommentSaveDisabled(): boolean {
        return this.isAddingComment || !this.newCommentControl.value.trim();
    }

    protected clearComment() {
        this.newCommentControl.setValue('');
        this.resizeCommentTextarea();
    }

    protected onCommentInput(event: Event) {
        const textarea = event.target as HTMLTextAreaElement | null;
        if (!textarea) {
            return;
        }

        this.resizeCommentTextarea(textarea);
        requestAnimationFrame(() => this.resizeCommentTextarea(textarea));
    }

    protected onCommentPaste() {
        requestAnimationFrame(() => this.resizeCommentTextarea());
    }

    protected onCommentsScroll(event: Event) {
        if (!this.isCommentsScrollEnabled) {
            return;
        }

        const el = event.target as HTMLElement | null;
        if (!el || !this.isAtCommentsBottom(el)) {
            return;
        }

        this.loadMoreComments();
    }

    protected onCommentsWheel(event: WheelEvent) {
        if (!this.isCommentsScrollEnabled) {
            return;
        }

        const el = event.currentTarget as HTMLElement | null;
        if (!el) {
            return;
        }

        // Requirement: only load more when user tries to scroll further down at the bottom.
        if (event.deltaY > 0 && this.isAtCommentsBottom(el)) {
            this.loadMoreComments();
        }
    }

    private loadComments(reset: boolean) {
        if (reset) {
            this.commentsPageIndex = 1;
            this.comments = [];
            this.totalCommentsCount = 0;
            this.hasMoreComments = true;
        }

        this.loadCommentsPage(this.commentsPageIndex, {
            append: !reset,
            scrollIntoView: reset,
        });
    }

    private watchTenantSettings() {
        this._subscriptions[SUBSCRIPTION_KEY_TENANT_SETTINGS] =
            this.tenantDataService.currentTenant$.subscribe({
                next: (tenant) => {
                    const configuredLimit =
                        tenant?.settings?.discrepancyCommentCharLimit;
                    this.commentMaxLength =
                        typeof configuredLimit === 'number' &&
                        configuredLimit > 0
                            ? configuredLimit
                            : COMMENT_MAX_LENGTH;

                    if (
                        this.newCommentControl.value.length >
                        this.commentMaxLength
                    ) {
                        this.newCommentControl.setValue(
                            this.newCommentControl.value.slice(
                                0,
                                this.commentMaxLength,
                            ),
                        );
                    }
                },
            });
    }

    private loadMoreComments() {
        if (!this.canLoadMoreComments()) {
            return;
        }

        this.commentsPageIndex += 1;
        this.loadComments(false);
    }

    private canLoadMoreComments() {
        if (this.activeTab !== 'comments') {
            return false;
        }
        if (this.isLoadingComments) {
            return false;
        }
        return this.hasMoreComments;
    }

    private loadCommentsPage(
        pageIndex: number,
        options: { append: boolean; scrollIntoView: boolean },
    ) {
        this.isLoadingComments = true;
        const params = {
            pageSize: this.commentsPageSize,
            pageIndex,
            order: this.commentsOrder,
        };

        this._subscriptions[SUBSCRIPTION_KEY_LOAD_COMMENTS] =
            this.discrepancyService
                .getDiscrepancyComments(this.discrepancyId, params)
                .subscribe({
                    next: (data) => {
                        this.totalCommentsCount = data.totalCount;

                        const incoming = data.comments ?? [];
                        if (options.append) {
                            const existingIds = new Set(
                                this.comments.map((c) => c.id),
                            );
                            const newOnes = incoming.filter(
                                (c) => !existingIds.has(c.id),
                            );
                            this.comments = [...this.comments, ...newOnes];
                        } else {
                            this.comments = incoming;
                        }

                        // If backend doesn't provide totalCount reliably, fall back to response length.
                        if (incoming.length < this.commentsPageSize) {
                            this.hasMoreComments = false;
                        }

                        if (options.scrollIntoView) {
                            this.scrollToArea();
                        }

                        this.isLoadingComments = false;
                    },
                    error: (error) => {
                        console.error(
                            `Failed to load comments for discrepancy ${this.discrepancyId}:`,
                            error,
                        );
                        this.isLoadingComments = false;
                        this.hasMoreComments = false;
                    },
                });
    }

    private isAtCommentsBottom(el: HTMLElement) {
        return (
            el.scrollTop + el.clientHeight >=
            el.scrollHeight - COMMENTS_BOTTOM_THRESHOLD_PX
        );
    }

    // ============================================================================
    // Activity Log
    // ============================================================================

    get activityLogButtonText() {
        return `${this.activeTab === 'activityLog' ? 'Hide' : 'Show'} Activity Log`;
    }

    get onlyActivityLog() {
        return (
            !this.isStationVariant && !this.showComments && !this.showEscalation
        );
    }

    protected toggleActivityLog() {
        this.disableCommentsScroll();
        if (this.activeTab === 'activityLog') {
            this.activeTab = null;
        } else {
            this.activeTab = 'activityLog';
        }
    }

    protected onActivityLogGridReady(event: GridReadyEvent) {
        this.activityLogGridApi = event.api;
        const datasource = this.getActivityLogs();
        this.activityLogGridApi.setGridOption(
            'serverSideDatasource',
            datasource,
        );
    }

    private getActivityLogQueryParams(request: IServerSideGetRowsRequest) {
        return {
            ...getSortQuery(request.sortModel),
        };
    }

    private getActivityLogs(): IServerSideDatasource {
        return {
            getRows: (params) => {
                params.api.hideOverlay();
                this.discrepancyService
                    .getDiscrepancyActivityLogs(
                        this.discrepancyId,
                        this.getActivityLogQueryParams(params.request),
                    )
                    .subscribe((data) => {
                        if (data) {
                            const dataWithLocalTime = data.map((item) => {
                                return {
                                    ...item,
                                    createdAt: convertToLocalTimeAndFormat(
                                        item.createdAt,
                                    ),
                                };
                            });
                            params.success({
                                rowData: dataWithLocalTime,
                                rowCount: dataWithLocalTime.length,
                            });
                            if (data.length === 0) {
                                params.api.showNoRowsOverlay();
                            }
                            this.scrollToActivityLogArea();
                        } else {
                            params.fail();
                        }
                    });
            },
        };
    }

    private getActivityLogColumnDefs(): ColDef[] {
        return [
            { field: 'user', sortable: false },
            {
                field: 'logMessage',
                headerName: 'Activity',
                flex: 2,
                sortable: false,
            },
            {
                field: 'createdAt',
                headerName: 'Date',
                resizable: false,
                sort: 'desc',
                sortingOrder: ['asc', 'desc'],
            },
        ];
    }

    // ============================================================================
    // Escalation
    // ============================================================================

    get escalationButtonText() {
        return `${this.activeTab === 'escalation' ? 'Hide' : 'Show'} Escalation`;
    }

    protected toggleEscalation() {
        this.disableCommentsScroll();
        if (this.activeTab === 'escalation') {
            this.activeTab = null;
        } else {
            this.activeTab = 'escalation';
            if (this.escalationLevels.length === 0) {
                this.loadEscalation();
            }
        }
    }

    getEscalationRecipientsCount(level: number) {
        const item = this.escalationLevels.find((i) => i.level === level);
        const ccCount = item?.ccRecipients.split(';').length ?? 0;
        const toCount = item?.toRecipients.split(';').length ?? 0;

        return ccCount + toCount;
    }

    private loadEscalation() {
        this.escalationError = false;

        this._subscriptions[SUBSCRIPTION_KEY_ESCALATION_LEVELS] =
            this.discrepancyService
                .getDiscrepancyEscalation(this.discrepancyId)
                .subscribe({
                    next: (response) => {
                        if (response.success) {
                            const data = response.data;
                            this.escalationStarted = data.state !== null;
                            this.escalationLevels = data.levels;
                        } else {
                            this.escalationError = true;
                        }
                    },
                    error: (error) => {
                        console.error(
                            `Failed to load escalation for discrepancy ${this.discrepancyId}:`,
                            error,
                        );
                        this.escalationError = true;
                    },
                });
    }

    private checkEscalationAvailability() {
        this.escalationError = false;
        this.isEscalationToggleVisible = false;

        this._subscriptions[SUBSCRIPTION_KEY_ESCALATION_START] =
            this.discrepancyService
                .getDiscrepancyEscalation(this.discrepancyId)
                .subscribe({
                    next: (response) => {
                        if (!response.success) {
                            this.escalationError = true;
                            this.isEscalationToggleVisible = false;
                            return;
                        }

                        const data = response.data;
                        this.escalationStarted = data.state !== null;
                        this.escalationLevels = data.levels;

                        // Requirement: if escalation doesn't exist (not started), hide the collapsible header.
                        this.isEscalationToggleVisible = this.escalationStarted;
                        if (
                            !this.isEscalationToggleVisible &&
                            this.activeTab === 'escalation'
                        ) {
                            this.activeTab = null;
                        }
                    },
                    error: () => {
                        this.escalationError = true;
                        this.isEscalationToggleVisible = false;
                    },
                });
    }

    // ============================================================================
    // Shared
    // ============================================================================

    private scrollToArea() {
        setTimeout(() => {
            if (this.contentEnd?.nativeElement) {
                this.contentEnd.nativeElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end',
                });
            }
        }, 100);
    }

    private scrollToActivityLogArea() {
        setTimeout(() => {
            if (this.activityLogEnd?.nativeElement) {
                this.activityLogEnd.nativeElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end',
                });
            }
        }, 100);
    }

    private resizeCommentTextarea(textarea?: HTMLTextAreaElement) {
        const element = textarea ?? this.commentTextarea?.nativeElement;
        if (!element) {
            return;
        }

        element.style.height = 'auto';
        element.style.height = `${element.scrollHeight}px`;
    }
}
